import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import IconButton from 'rsuite/esm/IconButton'
import Pagination from 'rsuite/Pagination'
import Table from 'rsuite/Table'
import Tag from 'rsuite/Tag'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'
import FileDownloadIcon from '@rsuite/icons/FileDownload'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import {LoanEligibilityLogModel, LoanEligibilityLogParams} from '../Model'
import * as loanEligibilityLogRedux from '../redux'
import {ILoanEligibilityLogState} from '../redux'

const Cell = Table.Cell

const STATUSES = [
  {value: 'eligible', label: 'Eligible'},
  {value: 'loan error', label: 'Age not within limits'},
  {value: 'loan and age error', label: 'Tenure exceeds age limit'},
  {value: 'negative value error', label: 'Insufficient income'},
]
const statusLabel = (s: string) => STATUSES.find((o) => o.value === s)?.label || s
const money = (n: number | null) =>
  n === null || n === undefined ? '' : Number(n).toLocaleString('en-IN', {maximumFractionDigits: 2})

const openBlob = (data: BlobPart, type: string, download?: string) => {
  const url = window.URL.createObjectURL(new Blob([data], {type}))
  if (!download) return window.open(url, '_blank')
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', download)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/** Loan-eligibility applicants (legacy backend loan_eligibility report): filters, letter PDF, Excel export. */
const LoanEligibilityLog = () => {
  const dispatch = useDispatch()
  const state: ILoanEligibilityLogState = useSelector((s: any) => s.loanEligibilityLog)
  const [params, setParams] = useState<LoanEligibilityLogParams>({page: 1, limit: 10})
  const [loanTypes, setLoanTypes] = useState<{id: string; title: string}[]>([])
  const [branches, setBranches] = useState<{id: string; title: string}[]>([])
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    loanEligibilityLogRedux.service
      .getLoanTypes()
      .then((r) => setLoanTypes(r?.data?.data?.loanType || []))
      .catch(() => {})
    loanEligibilityLogRedux.service
      .getBranches()
      .then((r) => setBranches(r?.data?.data?.branch || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    dispatch(loanEligibilityLogRedux.actions.getLoanEligibilityLog(params))
  }, [params])

  const filters = () => {
    const {page, limit, ...rest} = params
    return Object.fromEntries(Object.entries(rest).filter(([, v]) => v))
  }
  const setFilter = (name: keyof LoanEligibilityLogParams, value: string) =>
    setParams({...params, page: 1, [name]: value || undefined})

  const handleExport = () => {
    setExporting(true)
    loanEligibilityLogRedux.service
      .exportLoanEligibilityLog(filters())
      .then((r) =>
        openBlob(
          r.data,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          `EligibilityReport${new Date().toISOString().slice(0, 10)}.xlsx`
        )
      )
      .catch(() => toast.error('Export failed'))
      .finally(() => setExporting(false))
  }

  const viewLetter = (id: string) =>
    loanEligibilityLogRedux.service
      .getLetter(id)
      .then((r) => openBlob(r.data, 'application/pdf'))
      .catch(() => toast.error('Letter not available'))

  const columns = [
    {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
    {label: 'Reference No.', dataKey: 'referenceNumber', width: 130, cell: <Cell dataKey='referenceNumber' />},
    {label: 'Full Name', dataKey: 'fullName', width: 160, cell: <Cell dataKey='fullName' />},
    {label: 'Date of Birth', dataKey: 'dateOfBirth', width: 110, cell: <Cell dataKey='dateOfBirth' />},
    {label: 'Email', dataKey: 'emailAddress', width: 190, cell: <Cell dataKey='emailAddress' />},
    {label: 'Mobile', dataKey: 'mobileNumber', width: 110, cell: <Cell dataKey='mobileNumber' />},
    {label: 'Address', dataKey: 'address', width: 150, cell: <Cell dataKey='address' />},
    {label: 'Preferred Branch', dataKey: 'preferredBranch', width: 150, cell: <Cell dataKey='preferredBranch' />},
    {label: 'Loan Type', dataKey: 'loanType', width: 170, cell: <Cell dataKey='loanType' />},
    {label: 'Loan Amount', dataKey: 'loanAmount', width: 120, cell: <Cell dataKey='loanAmount' />},
    {label: 'Tenure (Years)', dataKey: 'tenure', width: 100, cell: <Cell dataKey='tenure' />},
    {label: 'Gross Monthly Income', dataKey: 'grossMonthlyIncome', width: 150, cell: <Cell dataKey='grossMonthlyIncome' />},
    {label: 'Existing Monthly EMI', dataKey: 'existingMonthlyEmi', width: 150, cell: <Cell dataKey='existingMonthlyEmi' />},
    {label: 'Purpose of Loan', dataKey: 'purposeOfLoan', width: 180, cell: <Cell dataKey='purposeOfLoan' />},
    {label: 'Eligible Amount', dataKey: 'principalAmount', width: 130, cell: <Cell dataKey='principalAmount' />},
    {
      label: 'Status',
      dataKey: 'status',
      width: 170,
      cell: (
        <Cell>
          {(row: any) => (
            <Tag color={row.status === 'eligible' ? 'green' : 'orange'} size='sm'>
              {statusLabel(row.status)}
            </Tag>
          )}
        </Cell>
      ),
    },
    {label: 'Applied On', dataKey: 'submittedAt', width: 150, cell: <Cell dataKey='submittedAt' />},
    {
      label: 'Letter',
      width: 80,
      align: 'center',
      cell: (
        <Cell>
          {(row: any) =>
            row.status === 'eligible' ? (
              <Whisper placement='top' trigger='hover' speaker={<Tooltip>View letter (PDF)</Tooltip>}>
                <IconButton
                  appearance='subtle'
                  aria-label='View letter'
                  onClick={() => viewLetter(row.id)}
                  icon={<FileDownloadIcon />}
                  data-cy='letter-button'
                />
              </Whisper>
            ) : null
          }
        </Cell>
      ),
    },
  ]

  const data = (state?.data?.loanEligibilityLog || []).map((item: LoanEligibilityLogModel, i: number) => ({
    ...item,
    sn: (params.page - 1) * params.limit + (i + 1),
    loanAmount: money(item.loanAmount),
    grossMonthlyIncome: money(item.grossMonthlyIncome),
    existingMonthlyEmi: money(item.existingMonthlyEmi),
    principalAmount: money(item.principalAmount),
    purposeOfLoan: item.otherPurposeOfLoan
      ? `${item.purposeOfLoan || ''} (${item.otherPurposeOfLoan})`
      : item.purposeOfLoan,
  }))

  const select = (
    name: keyof LoanEligibilityLogParams,
    label: string,
    options: {value: string; label: string}[]
  ) => (
    <div className='col-md-3 col-sm-6 mb-3'>
      <label className='form-label fw-bolder fs-7' htmlFor={`filter-${name}`}>
        {label}
      </label>
      <select
        id={`filter-${name}`}
        className='form-select form-select-sm'
        value={(params[name] as string) || ''}
        onChange={(e) => setFilter(name, e.target.value)}
      >
        <option value=''>All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
  const date = (name: 'fromDate' | 'toDate', label: string) => (
    <div className='col-md-3 col-sm-6 mb-3'>
      <label className='form-label fw-bolder fs-7' htmlFor={`filter-${name}`}>
        {label}
      </label>
      <input
        id={`filter-${name}`}
        type='date'
        className='form-control form-control-sm'
        value={params[name] || ''}
        onChange={(e) => setFilter(name, e.target.value)}
      />
    </div>
  )

  return (
    <div className='shadow p-3 bg-white rounded'>
      <DesignComponent
        moduleName='Loan Eligibility Report'
        addShow={false}
        deleteShow={false}
        toggleMultipleShow={false}
        importShow={false}
        exportShow={true}
        exportButtonName='Export Excel'
        handleExport={handleExport}
        isExporting={exporting}
        params={params}
        setParams={setParams}
        handleRefresh={() => dispatch(loanEligibilityLogRedux.actions.getLoanEligibilityLog(params))}
      />
      <div className='row mt-3' data-cy='loan-eligibility-filters'>
        {date('fromDate', 'From Date')}
        {date('toDate', 'To Date')}
        {select(
          'productManagerId',
          'Loan Type',
          loanTypes.map((t) => ({value: t.id, label: t.title}))
        )}
        {select(
          'branchId',
          'Preferred Branch',
          branches.map((b) => ({value: b.id, label: b.title}))
        )}
        {select('eligibilityStatus', 'Status', STATUSES)}
      </div>
      <div className='datatable'>
        <RSuiteTable columns={columns} data={data} showLoading={state?.loading} />
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          className='mt-5'
          maxButtons={5}
          size='sm'
          layout={['total', '-', 'limit', '|', 'pager', 'skip']}
          total={state?.data?.meta?.total || 0}
          limitOptions={[10, 20, 50, 100]}
          limit={params.limit}
          activePage={params.page}
          onChangePage={(page) => setParams({...params, page})}
          onChangeLimit={(limit) => setParams({...params, page: 1, limit})}
        />
      </div>
    </div>
  )
}

export default LoanEligibilityLog
